import { PDFDocument, PDFForm as PDFLibForm } from 'pdf-lib';
import { Form990Data as ImportedForm990Data, Officer as ImportedOfficer } from '../types/Form990Types';
import { api } from './api';
import { SignatureService } from './SignatureService';
import path from 'path';
import fs from 'fs/promises';

interface FormFieldMapping {
  field?: string;
  type: string;
  transform?: (value: any, formData?: Form990Data) => any;
  value?: any;
  formatter?: (data: any) => any;
  fields?: Record<string, string>;
}

interface Officer {
  name: string;
  title: string;
  averageHoursPerWeek: number;
  compensation: number;
  role: 'DIRECTOR' | 'OFFICER' | 'BOTH';
  isVoting?: boolean;
  isIndependent?: boolean;
}

interface Form990Data {
  organizationInfo?: {
    missionStatement?: string;
    employeesCount?: number;
    volunteersCount?: number;
    website?: string;
  };
  header?: {
    name?: string;
    ein?: string;
    dba?: string;
    website?: string;
    address?: {
      street?: string;
      suite?: string;
      city?: string;
      state?: string;
      zip?: string;
      cityStateZip?: string;
    };
  };
  data?: {
    responses?: {
      part3_q1?: { value: boolean };
      part3_q2?: any;
      part3_q3?: any;
      part3_q4?: any;
      part3_q5?: any;
      part3_q6?: { value: boolean };
      part3_q7?: { value: boolean };
      part4_q2?: { value: boolean };
      part4_q3?: { value: boolean };
      part4_q4?: { value: boolean };
      part4_q8?: { value: boolean };
      part6_q1?: { value: boolean };
    };
  };
  officers?: Officer[];
  part6_q3?: { value: boolean };
}

interface PDFForm {
  getTextField: (name: string) => { setText: (text: string) => void };
  getCheckBox: (name: string) => { check: () => void; uncheck: () => void };
  getField: (name: string) => any;
  getFields: () => any[];
  getDocument: () => any;
}

interface PDFTextField {
  setText(text: string): void;
}

interface PDFCheckBox {
  check(): void;
  uncheck(): void;
}

interface PDFField {
  constructor: { name: string };
  setText(text: string): void;
  check(): void;
  uncheck(): void;
  getName(): string;
}

interface PDFImage {
  // Properties needed for the image
}

interface PDFPage {
  drawImage(image: PDFImage, options: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): void;
}

export class Form990PDFHandler {
  private static instance: Form990PDFHandler;
  private templateCache: ArrayBuffer | null = null;
  private readonly TEMPLATE_PATH = '/assets/forms/f990.pdf';
  private formId: string | null = null;
  private fiscalSponsorId: string | null = null;
  private form990Data: Form990Data | null = null;
  private form: PDFForm | null = null;
  private templatePath: string;

  private readonly fieldMappings = {
    'header.name': { field: 'topmostSubform[0].Page1[0].LineC[0].f1_4[0]', type: 'text' },
    'header.ein': { field: 'topmostSubform[0].Page1[0].LinesD_E_F[0].f1_9[0]', type: 'text' },
    'header.address.street': { field: 'topmostSubform[0].Page1[0].LineC[0].f1_6[0]', type: 'text' },
    'header.address.suite': { field: 'topmostSubform[0].Page1[0].LineC[0].f1_7[0]', type: 'text' },
    'header.address.combined': { 
      field: 'topmostSubform[0].Page1[0].LineC[0].f1_8[0]', 
      type: 'text',
      getValue: (data: Form990Data) => `McKinney, TX 75071`  // Hardcoded for testing
    },
    'header.dba': { 
      field: 'topmostSubform[0].Page1[0].LineC[0].f1_5[0]', 
      type: 'text',
      value: '(See attached DBA list)'
    },
    'taxExempt.501c3': {
      field: 'topmostSubform[0].Page1[0].c1_10[0]',
      type: 'checkbox',
      value: true
    },
    'organizationInfo.website': {
      field: 'topmostSubform[0].Page1[0].f1_16[0]',
      type: 'text'
    },
    'startDate': {
      field: 'topmostSubform[0].Page1[0].f1_1[0]',
      type: 'text',
      value: '01/01'
    },
    'endDate': {
      field: 'topmostSubform[0].Page1[0].f1_2[0]',
      type: 'text',
      value: '12/31'
    },
    'shortYear': {
      field: 'topmostSubform[0].Page1[0].f1_3[0]',
      type: 'text',
      value: '24'
    },
    'organizationInfo.organizationType': {
      fields: {
        CORPORATION: 'topmostSubform[0].Page1[0].c1_12[0]',
        TRUST: 'topmostSubform[0].Page1[0].c1_12[1]',
        ASSOCIATION: 'topmostSubform[0].Page1[0].c1_12[2]',
        OTHER: 'topmostSubform[0].Page1[0].c1_12[3]'
      },
      type: 'checkbox',
      transform: (orgType: string) => {
        // Ensure orgType is uppercase for comparison
        const type = orgType?.toUpperCase();
        return {
          'topmostSubform[0].Page1[0].c1_12[0]': type === 'CORPORATION',
          'topmostSubform[0].Page1[0].c1_12[1]': type === 'TRUST',
          'topmostSubform[0].Page1[0].c1_12[2]': type === 'ASSOCIATION',
          'topmostSubform[0].Page1[0].c1_12[3]': type === 'OTHER'
        };
      }
    },
    'organizationInfo.organizationType.corporation': {
      field: 'topmostSubform[0].Page1[0].c1_12[0]',
      type: 'checkbox',
      value: true
    },
    'organizationInfo.organizationType.trust': {
      field: 'topmostSubform[0].Page1[0].c1_12[1]',
      type: 'checkbox',
      value: false
    },
    'organizationInfo.organizationType.association': {
      field: 'topmostSubform[0].Page1[0].c1_12[2]',
      type: 'checkbox',
      value: false
    },
    'organizationInfo.organizationType.other': {
      field: 'topmostSubform[0].Page1[0].c1_12[3]',
      type: 'checkbox',
      value: false
    },
    'organizationInfo.yearFormed': {
      field: 'topmostSubform[0].Page1[0].f1_18[0]',
      type: 'text',
      transform: (year: number | string) => year.toString()
    },
    'organizationInfo.stateDomicile': {
      field: 'topmostSubform[0].Page1[0].f1_19[0]',
      type: 'text',
      transform: (state: string) => state.toUpperCase()
    },
    'organizationInfo.stateOfFormation': {
      field: 'topmostSubform[0].Page1[0].f1_19[0]',
      type: 'text'
    },
    'header.address.cityStateZip': {
      field: 'topmostSubform[0].Page1[0].LineC[0].f1_8[0]',
      type: 'text',
      transform: (_: any, formData: Form990Data) => {
        const address = formData?.header?.address;
        if (!address?.city || !address?.state || !address?.zip) {
          return '';
        }
        return `${address.city}, ${address.state} ${address.zip}`;
      }
    },
    'organizationInfo.missionStatement': {
      field: 'topmostSubform[0].Page1[0].Line1Entry[0].f1_20[0]',
      type: 'text'
    }
  };

  // Add a static create method if the constructor is private
  public static create(templatePath?: string): Form990PDFHandler {
    return new Form990PDFHandler(templatePath);
  }

  constructor(templatePath?: string) {
    this.templatePath = templatePath || path.join(process.cwd(), 'templates', 'form990.pdf');
  }

  private async loadTemplate(): Promise<ArrayBuffer> {
    try {
      if (this.templateCache) {
        return this.templateCache;
      }

      console.log('Loading Form 990 template from:', this.templatePath);
      const response = await fetch(this.templatePath);
      
      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      this.templateCache = arrayBuffer;
      return arrayBuffer;
    } catch (error) {
      console.error('Error loading Form 990 template:', error);
      throw new Error('Failed to load Form 990 template');
    }
  }

  private logAllFields(form: PDFForm) {
    const fields = form.getFields();
    console.log('All form fields:', fields.map((f: PDFField) => ({
      name: f.constructor.name,
      type: f.constructor.name
    })));
  }

  private async fillFormFields(form: PDFForm, data: Form990Data) {
    console.log('Full Form990 Data:', data);
    try {
      // Add debug logging
      console.log('Received Form990Data:', data);
      console.log('Organization Info:', data.organizationInfo);

      this.logAllFields(form);
      
      // Add debugging to print all fields
      const fields = form.getFields();
      console.log('All PDF field names:', fields.map(f => f.getName()));

      // Add specific debugging for EIN-related fields
      console.log("Fields containing 'EIN' or 'employer':", 
        fields.filter(f => 
          f.getName().toLowerCase().includes('ein') || 
          f.getName().toLowerCase().includes('employer')
        ).map(f => f.getName())
      );

      // For each field mapping, extract the value from the data and set it in the form
      for (const [dataPath, fieldInfo] of Object.entries(this.fieldMappings)) {
        try {
          // Get the value from the data object using the path
          let value = this.getNestedValue(data, dataPath);
          
          // For cityStateZip, we want to use the transform with the full data
          if (dataPath === 'header.address.cityStateZip') {
            // Use type assertion to handle the special case
            const fieldWithTransform = fieldInfo as { transform?: (val: any, data: Form990Data) => string };
            const transformedValue = fieldWithTransform.transform?.(null, data);
            if (transformedValue) {
              const fieldName = 'topmostSubform[0].Page1[0].LineC[0].f1_8[0]';
              const field = form.getField(fieldName);
              field.setText(transformedValue);
              console.log(`Set ${fieldName} to ${transformedValue}`);
            }
            continue;
          }
          
          console.log(`Processing ${dataPath}, found value:`, value);
          
          if (value === undefined && 'value' in fieldInfo && fieldInfo.value !== undefined) {
            value = fieldInfo.value;
          }

          if (value === undefined) {
            console.log(`No value found for ${dataPath}, skipping`);
            continue;
          }

          if ('transform' in fieldInfo && fieldInfo.transform) {
            value = fieldInfo.transform(value, data);
          }

          // Handle checkbox group mapping
          if ('fields' in fieldInfo && fieldInfo.fields && typeof value === 'string') {
            // Use type assertion for the transform method
            const fieldWithTransform = fieldInfo as { transform?: (val: string) => Record<string, boolean> };
            const checkboxValues = fieldWithTransform.transform ? 
              fieldWithTransform.transform(value) : {};
            for (const [fieldName, shouldCheck] of Object.entries(checkboxValues)) {
              try {
                const field = form.getField(fieldName);
                if (shouldCheck) {
                  field.check();
                } else {
                  field.uncheck();
                }
              } catch (error) {
                console.error(`Error setting checkbox ${fieldName}:`, error);
              }
            }
          } else {
            // Handle regular fields as before
            if ('field' in fieldInfo) {
              console.log(`Attempting to fill ${fieldInfo.field} with value: ${value}`);
              const field = form.getField(fieldInfo.field);
              
              if (fieldInfo.type === 'checkbox') {
                if (value) {
                  field.check();
                } else {
                  field.uncheck();
                }
              } else if (field.constructor.name === 'PDFTextField') {
                field.setText(value.toString());
              }
              
              console.log(`Successfully set ${fieldInfo.field} to ${value}`);
            }
          }
        } catch (error) {
          console.error(`Error setting field ${dataPath}:`, error);
        }
      }

      // Website field (Line J)
      const website = data.organizationInfo?.website || data.header?.website || '?';
      console.log('Processing website:', website);
      form.getTextField('topmostSubform[0].Page1[0].f1_16[0]').setText(website.toString());
      console.log('Successfully set website to:', website);

      // Calculate voting members count
      const votingMembersCount = data.data?.responses?.part3_q2 !== undefined ? 
        data.data.responses.part3_q2 : 
        data.officers?.filter(o => o.isVoting)?.length !== undefined ?
        data.officers.filter(o => o.isVoting).length :
        '?';
      
      // Calculate independent voting members count
      const independentVotingCount = data.data?.responses?.part3_q3 !== undefined ?
        data.data.responses.part3_q3 :
        data.officers?.filter(o => o.isVoting && o.isIndependent)?.length !== undefined ?
        data.officers.filter(o => o.isVoting && o.isIndependent).length :
        '?';

      // Page 1 mappings
      console.log('Processing voting members count:', votingMembersCount);
      form.getTextField('topmostSubform[0].Page1[0].f1_21[0]').setText(votingMembersCount.toString());
      console.log('Successfully set voting members count to:', votingMembersCount);

      console.log('Processing independent voting members count:', independentVotingCount);
      form.getTextField('topmostSubform[0].Page1[0].f1_22[0]').setText(independentVotingCount.toString());
      console.log('Successfully set independent voting members count to:', independentVotingCount);

      // Part VI Questions 1a & 1b - Use same values as Page 1
      console.log('Processing Part VI voting member counts:', {
        total: votingMembersCount,
        independent: independentVotingCount
      });

      // Question 1a - Total voting members
      form.getTextField('topmostSubform[0].Page6[0].f6_1[0]').setText(votingMembersCount.toString());
      console.log('Set Part VI voting members count to:', votingMembersCount);

      // Question 1b - Independent voting members
      form.getTextField('topmostSubform[0].Page6[0].f6_2[0]').setText(independentVotingCount.toString());
      console.log('Set Part VI independent voting members count to:', independentVotingCount);

      // Total employees count (Line 5)
      const employeesCount = data.data?.responses?.part3_q4 !== undefined ?
        data.data.responses.part3_q4 :
        data.organizationInfo?.employeesCount !== undefined ?
        data.organizationInfo.employeesCount :
        '?';

      console.log('Processing total employees count:', employeesCount);
      form.getTextField('topmostSubform[0].Page1[0].f1_23[0]').setText(employeesCount.toString());
      console.log('Successfully set total employees count to:', employeesCount);

      // Total volunteers count (Line 6)
      const volunteersCount = data.data?.responses?.part3_q5 !== undefined ?
        data.data.responses.part3_q5 :
        data.organizationInfo?.volunteersCount !== undefined ?
        data.organizationInfo.volunteersCount :
        '?';

      console.log('Processing total volunteers count:', volunteersCount);
      form.getTextField('topmostSubform[0].Page1[0].f1_24[0]').setText(volunteersCount.toString());
      console.log('Successfully set total volunteers count to:', volunteersCount);

      // Mission Statement (Part I, Line 1)
      const missionStatement = data.organizationInfo?.missionStatement || '?';
      console.log('Processing organizationInfo.missionStatement:', missionStatement);
      form.getTextField('topmostSubform[0].Page1[0].Line1Entry[0].f1_20[0]').setText(missionStatement);
      console.log('Successfully set Part I mission statement to:', missionStatement);

      // Reuse mission statement for Part III, Line 1
      console.log('Processing Part III mission statement:', missionStatement);
      form.getTextField('topmostSubform[0].Page2[0].Line1Entry[0].f2_1[0]').setText(missionStatement);
      console.log('Successfully set Part III mission statement to:', missionStatement);

      // Part III Question 2 - New Program Services
      const newProgramServices = data.data?.responses?.part3_q6?.value;
      console.log('Processing Part III new program services response:', newProgramServices);
      if (newProgramServices === true) {
        form.getCheckBox('topmostSubform[0].Page2[0].c2_2[0]').check();
      } else {
        form.getCheckBox('topmostSubform[0].Page2[0].c2_2[1]').check();
      }

      // Part III Question 3 - Changes to Program Services
      const programChanges = data.data?.responses?.part3_q7?.value;
      console.log('Processing Part III program changes response:', programChanges);
      if (programChanges === true) {
        form.getCheckBox('topmostSubform[0].Page2[0].c2_3[0]').check();
      } else {
        form.getCheckBox('topmostSubform[0].Page2[0].c2_3[1]').check();
      }

      // Part IV Question 1 - 501(c)(3) Status
      console.log('Setting 501(c)(3) status to YES');
      form.getCheckBox('topmostSubform[0].Page3[0].Question1_ReadOrder[0].c3_1[0]').check();

      // Part IV Question 2 - Schedule B Requirement
      console.log('Processing Schedule B requirement response:', data.data?.responses?.part4_q2?.value);
      if (data.data?.responses?.part4_q2?.value === true) {
        form.getCheckBox('topmostSubform[0].Page3[0].c3_2[0]').check();
      } else {
        form.getCheckBox('topmostSubform[0].Page3[0].c3_2[1]').check();
      }

      // Part IV Question 3 - Political Campaign Activities
      console.log('Processing political campaign activities response:', data.data?.responses?.part4_q3?.value);
      if (data.data?.responses?.part4_q3?.value === true) {
        form.getCheckBox('topmostSubform[0].Page3[0].c3_3[0]').check();
      } else {
        form.getCheckBox('topmostSubform[0].Page3[0].c3_3[1]').check();
      }

      // Part IV Question 4 - Lobbying Activities
      console.log('Processing lobbying activities response:', data.data?.responses?.part4_q4?.value);
      if (data.data?.responses?.part4_q4?.value === true) {
        form.getCheckBox('topmostSubform[0].Page3[0].c3_4[0]').check();
      } else {
        form.getCheckBox('topmostSubform[0].Page3[0].c3_4[1]').check();
      }

      // Part IV Question 8 - Art/Historical Collections
      console.log('Processing art collections response:', data.data?.responses?.part4_q8?.value);
      if (data.data?.responses?.part4_q8?.value === true) {
        form.getCheckBox('topmostSubform[0].Page3[0].c3_8[0]').check();
      } else {
        form.getCheckBox('topmostSubform[0].Page3[0].c3_8[1]').check();
      }

      // Part IV Question 30 - Art/Historical Contributions
      console.log('Processing art contributions response:', data.data?.responses?.part4_q8?.value);
      if (data.data?.responses?.part4_q8?.value === true) {
        form.getCheckBox('topmostSubform[0].Page4[0].c4_18[0]').check();
      } else {
        form.getCheckBox('topmostSubform[0].Page4[0].c4_18[1]').check();
      }

      // Part IV Questions 31 & 32 - Use same response as Part III Question 1
      console.log('Processing liquidation/asset disposal response:', data.data?.responses?.part3_q1?.value);
      
      // Question 31 - Liquidate/terminate operations
      if (data.data?.responses?.part3_q1?.value === true) {
        form.getCheckBox('topmostSubform[0].Page4[0].c4_19[0]').check();
      } else {
        form.getCheckBox('topmostSubform[0].Page4[0].c4_19[1]').check();
      }

      // Question 32 - Dispose of >25% net assets
      if (data.data?.responses?.part3_q1?.value === true) {
        form.getCheckBox('topmostSubform[0].Page4[0].c4_20[0]').check();
      } else {
        form.getCheckBox('topmostSubform[0].Page4[0].c4_20[1]').check();
      }

      // Part VI Question 4 - Changes to governing documents
      console.log('Processing governing document changes:', data.data?.responses?.part6_q1?.value);
      if (data.data?.responses?.part6_q1?.value === true) {
        form.getCheckBox('topmostSubform[0].Page6[0].c6_4[0]').check();
      } else {
        form.getCheckBox('topmostSubform[0].Page6[0].c6_4[1]').check();
      }

      // Part VI Section B Question 12a - Written conflict of interest policy
      const hasConflictPolicy = data.part6_q3?.value === true;
      console.log('Processing conflict of interest policy:', {
        fullData: data,
        questionResponse: data.part6_q3,
        hasPolicy: hasConflictPolicy,
        rawValue: data.part6_q3?.value
      });
      
      // Update checkbox field ID to match PDF structure
      if (hasConflictPolicy !== undefined) {
        form.getCheckBox('topmostSubform[0].Page6[0].c6_15[0]').check();
      } else {
        form.getCheckBox('topmostSubform[0].Page6[0].c6_15[1]').check();
      }

      // Fill officers table
      if (data.officers && data.officers.length > 0) {
        await this.fillOfficersTable(form, data.officers);
      }

    } catch (error) {
      console.error('Error in fillFormFields:', error);
      throw error;
    }
  }

  private getNestedValue(obj: any, path: string): any {
    console.log(`Getting nested value for path: ${path}`);
    const value = path.split('.').reduce((current, key) => 
      current && current[key] !== undefined ? current[key] : undefined, obj);
    console.log(`Found value:`, value);
    return value;
  }

  public async generateForm990(data: Form990Data): Promise<ArrayBuffer> {
    try {
      console.log('Loading PDF template from:', this.templatePath);
      
      // Load the PDF template
      const templateBytes = await fs.readFile(this.templatePath);
      const pdfDoc = await PDFDocument.load(templateBytes);
      
      // Get the form from the PDF
      const form = pdfDoc.getForm();
      console.log('Got form from PDF');
      
      // Cast the form to our PDFForm interface
      await this.fillFormFields(form as unknown as PDFForm, data);
      console.log('Fields filled successfully');
      
      const filledPdfBytes = await pdfDoc.save();
      return filledPdfBytes.buffer as ArrayBuffer;
    } catch (error) {
      console.error('Error generating Form 990 PDF:', error);
      throw error;
    }
  }

  private prepareFormData(data: any): any {
    return {
      ...data,
      metadata: {
        ...data.metadata,
        website: 'www.causeandimpact.org'
      }
    };
  }

  private processField(path: string, data: Form990Data): void {
    // Special case for city/state/zip combination
    if (path === 'header.address.zip') {
      const address = data.header?.address;
      if (address) {
        const combinedValue = `${address.city}, ${address.state} ${address.zip}`;
        const field = 'topmostSubform[0].Page1[0].LineC[0].f1_8[0]';
        this.setFieldValue(field, combinedValue);
        console.log('Set combined address:', combinedValue);
      }
      return;
    }

    const value = this.getNestedValue(data, path);
    // ... rest of existing processField function ...
  }

  private setFieldValue(fieldName: string, value: any): void {
    try {
      // Special case for the city/state/zip field
      if (fieldName === 'topmostSubform[0].Page1[0].LineC[0].f1_8[0]') {
        const address = this.form990Data?.header?.address;
        if (address?.city && address?.state && address?.zip) {
          value = `${address.city}, ${address.state} ${address.zip}`;
          console.log('Setting combined address to:', value);
        }
      }

      const field = this.form?.getField(fieldName);
      if (field) {
        if (typeof value === 'boolean') {
          field.check();
        } else {
          field.setText(value?.toString() || '');
        }
        console.log(`Successfully set ${fieldName} to ${value}`);
      }
    } catch (error) {
      console.error(`Error setting field ${fieldName}:`, error);
    }
  }

  async initialize(fiscalSponsorId: string) {
    this.fiscalSponsorId = fiscalSponsorId;
    console.log('Initializing Form 990 PDF Handler for:', fiscalSponsorId);
  }

  async loadPreview(formId: string) {
    try {
      this.formId = formId;
      console.log('Loading Form 990 preview for:', formId);
      
      // Fetch the form data
      const response = await api.form990.getDrafts();
      
      if (response && response.length > 0) {
        const form = response.find((f: any) => f._id === formId);
        if (form) {
          return {
            success: true,
            data: form,
            message: 'Preview loaded successfully'
          };
        }
      }
      
      throw new Error('Form not found');
    } catch (error) {
      console.error('Error loading Form 990 preview:', error);
      return {
        success: false,
        message: 'Failed to load preview',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  public async generatePDF(data: Form990Data): Promise<Buffer> {
    try {
      // Load the PDF template
      const templatePath = path.resolve(__dirname, '../templates/f990ez.pdf');
      const templateBytes = await fs.readFile(templatePath);
      
      // Parse the PDF document
      const pdfDoc = await PDFDocument.load(templateBytes);
      const originalForm = pdfDoc.getForm();
      console.log('Got form from PDF');
      
      // Create a wrapper that adapts the PDF library's form to our interface
      const formWrapper = {
        getTextField: (name: string) => originalForm.getTextField(name),
        getCheckBox: (name: string) => originalForm.getCheckBox(name),
        getField: (name: string) => {
          const field = originalForm.getField(name);
          // Add the missing methods to the field
          return {
            ...field,
            setText: (text: string) => {
              if (field.constructor.name === 'PDFTextField') {
                (field as any).setText(text);
              }
            },
            check: () => {
              if (field.constructor.name === 'PDFCheckBox') {
                (field as any).check();
              }
            },
            uncheck: () => {
              if (field.constructor.name === 'PDFCheckBox') {
                (field as any).uncheck();
              }
            },
            getName: () => field.getName()
          };
        },
        getFields: () => originalForm.getFields(),
        getDocument: () => pdfDoc
      };
      
      // Use our wrapper instead of the original form
      await this.fillFormFields(formWrapper as PDFForm, data);
      console.log('Fields filled successfully');
      
      // Save the filled PDF
      const filledPdfBytes = await pdfDoc.save();
      return Buffer.from(filledPdfBytes);
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  private mapFormDataToPDFFields(data: Form990Data): Record<string, string> {
    const pdfFields: Record<string, string> = {};
    console.log('Mapping Form 990 data to PDF fields...');

    try {
      // ... existing mappings ...

      // Part I - Line 3 - Number of voting members
      console.log('Getting nested value for path: data.responses.part3_q2');
      const votingMembers = data.data?.responses?.part3_q2 !== undefined ? 
        data.data.responses.part3_q2 : 
        data.officers?.filter(o => o.isVoting)?.length !== undefined ?
        data.officers.filter(o => o.isVoting).length :
        '?';
      
      console.log('Processing voting members count, found value:', votingMembers);
      this.setFormField(pdfFields, 'topmostSubform[0].Page1[0].f1_21[0]', votingMembers.toString());

      // ... rest of existing mappings ...
    } catch (error) {
      console.error('Error in mapFormDataToPDFFields:', error);
    }

    return pdfFields;
  }

  private setFormField(fields: Record<string, string>, fieldName: string, value: string) {
    console.log(`Attempting to fill ${fieldName} with value: ${value}`);
    fields[fieldName] = value;
    console.log(`Successfully set ${fieldName} to ${value}`);
  }

  private async fillOfficersTable(form: PDFForm, officers: Officer[]) {
    try {
      // Map of row numbers to their field IDs
      const rowFieldMappings: Record<number, {
        name: string;
        title: string;
        hours: string;
        director: string;
        officer: string;
        compensation: string;
      }> = {
        1: {
          name: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow1[0].One[0].f7_1[0]',
          title: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow1[0].One[0].f7_2[0]',
          hours: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow1[0].One_B[0].f7_3[0]',
          director: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow1[0].c7_3[0]',
          officer: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow1[0].c7_5[0]',
          compensation: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow1[0].f7_5[0]'
        },
        2: {
          name: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow2[0].Two[0].f7_8[0]',
          title: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow2[0].Two[0].f7_9[0]',
          hours: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow2[0].Two_B[0].f7_10[0]',
          director: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow2[0].c7_9[0]',
          officer: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow2[0].c7_11[0]',
          compensation: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow2[0].f7_12[0]'
        },
        3: {
          name: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow3[0].Three[0].f7_14[0]',
          title: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow3[0].Three[0].f7_15[0]',
          hours: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow3[0].Three_B[0].f7_16[0]',
          director: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow3[0].c7_15[0]',
          officer: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow3[0].c7_17[0]',
          compensation: 'topmostSubform[0].Page7[0].Table_Page7[0].BodyRow3[0].f7_18[0]'
        }
      };

      officers.forEach((officer, index) => {
        const rowNum = index + 1;
        const fields = rowFieldMappings[rowNum as keyof typeof rowFieldMappings];
        
        if (fields) {
          // Fill in basic information
          form.getTextField(fields.name).setText(officer.name);
          form.getTextField(fields.title).setText(officer.title);
          form.getTextField(fields.hours).setText(officer.averageHoursPerWeek.toString());
          form.getTextField(fields.compensation).setText(officer.compensation.toString());

          // Handle role checkboxes
          if (officer.role === 'DIRECTOR' || officer.role === 'BOTH') {
            form.getCheckBox(fields.director).check();
          }
          if (officer.role === 'OFFICER' || officer.role === 'BOTH') {
            form.getCheckBox(fields.officer).check();
          }
        }
      });

    } catch (error) {
      console.error('Error filling officers table:', error);
      throw error;
    }
  }

  private async addSignature(
    form: PDFForm, 
    signatureData: {
      signature: string;
      title: string;
      date: string;
    }
  ) {
    try {
      // Officer signature fields on page 1
      const signatureService = new SignatureService();
      const { signatureImage } = await signatureService.createSignature({
        type: 'DRAWN',
        data: signatureData.signature,
        signerId: 'officer',
        timestamp: new Date()
      });

      // Add signature image - handle the case where getDocument is not available
      let pdfDoc;
      if (form.getDocument) {
        pdfDoc = form.getDocument();
      } else {
        // Access the document through the form's internal properties
        // This is a workaround since the actual PDF library doesn't expose getDocument
        pdfDoc = (form as any)._document || (form as any).doc;
      }
      
      const signatureEmbed = await pdfDoc.embedPng(signatureImage);
      const page = pdfDoc.getPage(0);

      // Position signature on form
      page.drawImage(signatureEmbed, {
        x: 350,  // Adjust these coordinates based on the actual form
        y: 150,
        width: 150,
        height: 50
      });

      // Fill in other signature fields
      form.getTextField('topmostSubform[0].Page1[0].SignTitle[0]').setText(signatureData.title);
      form.getTextField('topmostSubform[0].Page1[0].SignDate[0]').setText(signatureData.date);

    } catch (error) {
      console.error('Error adding signature:', error);
      throw error;
    }
  }
}